import {computed, reactive} from "vue";
import {WechatMerchant} from "@/model/wechat_merchant";
import {message, Modal, notification} from "ant-design-vue";
import {Activity_old, IActivityData} from "@/model/activity_old";
import {api} from "@/lib/api";
import {ITheme} from "@/model/materials/themes";
import {IWechatMerchantApplyment} from "@/model/wechat_merchant_applyment";
import {IAgentCoupon, ServiceOrder, TServiceType} from "@/model/order";
import emit from "@/lib/emit";
import {AgentWallet} from "@/model/agent_wallet";
import {IBranch} from "@/model/branch";
import {IEmployee} from "@/model/employee";
import {Brand, IBrand} from "@/model/brand";

export enum EVipLevel {Trial = "体验版", Basic = "基础版", Professional = "专业版", Flagship = "旗舰版"}

export interface IAccount {
    agent_id: string;
    phone_number: string;
    wx_union_id: string;
    browser_id: string;
    level: EVipLevel;
    expiration: string;
    wallet_balance: number;
    custom_ad_chance_times: number;
    online_teach_times: number;
    productions_times: number;
    active_brand_id: string | null;
}

export interface IAgentTheme extends ITheme {
    agent_id: string;
    service_order_id: string;
    activity_id: string | null;
}

export interface IServiceOrder {
    order_id: string;
    agent_id: string;
    service: TServiceType;
    wepay_order: any;
    complete_at: string | null;
}

export interface IAgentData {
    account: IAccount;
    brands: IBrand[];
    activities: IActivityData[];
    wechat_merchants: { [key: string]: IWechatMerchantApplyment };
    themes: IAgentTheme[];
    orders: IServiceOrder[];
    coupons: IAgentCoupon[];
    service: { [key: string]: IServiceBasePrice }
}

export interface IServiceBasePrice {
    original: number;
    deduction: number;
}

interface IServiceEvent {
    order: IServiceOrder;
    theme?: IAgentTheme;
}

export class UserAgent {
    public readonly meta = reactive({
        account: {} as IAccount,
        brands: [] as IBrand[],
        activities: [] as Activity_old[],
        themes: [] as IAgentTheme[],
        orders: [] as IServiceOrder[],
        coupons: [] as IAgentCoupon[],
        service: {} as { [key: string]: IServiceBasePrice },
    })
    readonly wechatMerchant: WechatMerchant

    readonly currentBrand = computed(() => this.meta.brands.find(b => b.brand_id == this.meta.account.active_brand_id))
    readonly brands: { [key: string]: Brand } = {}
    employees: IEmployee[] | null = null

    constructor(agent: IAgentData) {
        this.meta.account = agent.account;
        // this.active.activities = agent.activities.map((ad: IActivityData) => new Activity_old(ad, this));
        this.meta.brands = agent.brands ?? [];
        this.meta.themes = agent.themes ?? [];
        this.meta.orders = agent.orders ?? [];
        this.meta.coupons = agent.coupons ?? [];
        this.meta.service = agent.service ?? [];
        this.wechatMerchant = new WechatMerchant(this, agent.wechat_merchants)
        agent.brands.forEach(b => this.brands[b.brand_id] = new Brand(this, b))
        if (this.meta.brands.length > 0 && this.meta.account.active_brand_id == null) this.setActiveBrand(this.meta.brands[0].brand_id).then()
        emit.on('service', rs => {
            console.log(rs)
            const evt = rs as IServiceEvent
            this.meta.orders.unshift(evt.order)
            if (evt.theme) this.meta.themes.unshift(evt.theme)
            notification.success({message: `${evt.order.service} 下单成功`, description: '。。。。'})
        })
        emit.on('wallet', (rs) => {
            const res = rs as any;
            notification.success({message: '充值成功', description: `成功充值${res.amount}元`})
        })
    }

    update(agent: IAgentData) {
        this.meta.account = agent.account
        this.meta.brands = agent.brands;
        // this.active.activities = agent.activities.map((ad: IActivityData) => new Activity_old(ad, this));
        this.meta.themes = agent.themes ?? [];
        this.meta.orders = agent.orders ?? [];
        this.meta.coupons = agent.coupons ?? [];
        this.meta.service = agent.service ?? [];
        this.wechatMerchant.update(agent.wechat_merchants)
        agent.brands.forEach(b => this.brands[b.brand_id] = new Brand(this, b))
    }

    brand(brand_id: string): IBrand | undefined {
        return this.meta.brands.find(b => b.brand_id == brand_id)
    }

    brandByBranchId(branch_id: string): IBrand | undefined {
        return this.meta.brands.find(b => b.branches.find(br => br.branch_id == branch_id))
    }

    branch(branch_id: string): IBranch | undefined {
        for (const brand of this.meta.brands) {
            const br = brand.branches.find(b => b.branch_id == branch_id)
            if (br) return br
        }
        return undefined
    }

    async phoneNumberSmsCaptcha(captchaVerifyParam: string, pn: string): Promise<any> {
        return api({
            url: `user/${this.meta.account.agent_id}/bind/phone/number/sms/captcha`,
            method: 'POST',
            data: {param: captchaVerifyParam, mobile: pn}
        })
    }

    async bindPhoneNumber(phone_number: string, code: string): Promise<any> {
        return api({url: `user/${this.meta.account.agent_id}/bind/phone/number`, method: 'POST', data: {phone_number, code}})
            .then(rs => {
                this.meta.account.phone_number = phone_number
                return rs;
            })
    }

    async bindAppletQR(): Promise<Blob> {
        return await api({url: `user/${this.meta.account.agent_id}/bind/applet/qr`, responseType: 'blob'})
    }

    async setActiveBrand(brand_id: string): Promise<void> {
        const rs = await api<void, void>({url: 'user', method: 'PATCH', data: {brand_id}})
        this.meta.account.active_brand_id = brand_id
        return rs
    }

    async newBrand(data: never): Promise<any> {
        return api<void, IBrand>({url: `user/${this.meta.account.agent_id}/brand`, method: 'POST', data: data})
            .then(b => this.meta.brands.unshift(b))
    }

    authorizer(): Authorizer {
        return new Authorizer(this)
    }

    async location(): Promise<Location> {
        return api({url: `user/${this.meta.account.agent_id}/location`})
    }

    async geocoder(d: any): Promise<Location> {
        return api({url: `user/${this.meta.account.agent_id}/geocoder`, params: d})
    }

    async initBrand(data: object): Promise<IBrand> {
        return api({url: `user/${this.meta.account.agent_id}/brand/init`, method: 'POST', data: data})
    }

    async newActivity(data: object): Promise<IActivityData> {
        return api({url: `user/${this.meta.account.agent_id}/activity`, method: 'POST', data: data})
    }

    async appletUploadQR(id: string): Promise<Blob> {
        return api({url: `user/${this.meta.account.agent_id}/applet/qr/upload`, params: {id}, responseType: 'blob'})
    }

    async decodeQrImage(url: string): Promise<{ [key: string]: string }> {
        return api({url: `user/${this.meta.account.agent_id}/wechat/qr/decode`, method: 'POST', data: {url}})
    }

    newOrder(service: TServiceType, activity_id?: string): Promise<ServiceOrder> {
        return new Promise((resolve, reject) => {
            try {
                resolve(new ServiceOrder(service, this, activity_id))
            } catch (err) {
                reject(err)
            }
        })
    }

    newWallet(): AgentWallet {
        return new AgentWallet(this)
    }

    async loadEmployees(branch_ids: string[]): Promise<IEmployee[]> {
        if (this.employees === null) {
            const done = message.loading('载入中', 0)
            this.employees = await api<void, IEmployee[]>({url: `user/${this.meta.account.agent_id}/employees`}).finally(done)
        }
        return (this.employees ?? []).filter(e => branch_ids.includes(e.branch_id))
    }
}

class Authorizer {
    constructor(private readonly userAgent: UserAgent) {
    }

    brand(fn: () => void) {
        switch (this.userAgent.meta.account.level) {
            case EVipLevel.Trial:
                Modal.confirm({
                    title: '不行', content: '内容', onOk() {
                        //
                    }
                })
                break
            case EVipLevel.Basic:
                break;
            case EVipLevel.Professional:
                break;
            case EVipLevel.Flagship:
                fn()
                break;
        }
    }

    branch(fn: () => void) {
        switch (this.userAgent.meta.account.level) {
            case EVipLevel.Trial:
                Modal.confirm({
                    title: '不行', content: '内容', onOk() {
                        //
                    }
                })
                break
            case EVipLevel.Basic:
                break;
            case EVipLevel.Professional:
                break;
            case EVipLevel.Flagship:
                fn()
                break;
        }
    }

    activityPublish(fn: () => void) {
        switch (this.userAgent.meta.account.level) {
            case EVipLevel.Trial:
                Modal.confirm({
                    title: '不行', content: '内容', onOk() {
                        //
                    }
                })
                break
            case EVipLevel.Basic:
                break;
            case EVipLevel.Professional:
                break;
            case EVipLevel.Flagship:
                fn()
                break;
        }
    }

    grant(l: EVipLevel): boolean {
        switch (l) {
            case EVipLevel.Trial:
                return true;
            case EVipLevel.Basic:
                return [EVipLevel.Flagship, EVipLevel.Professional, EVipLevel.Basic].includes(this.userAgent.meta.account.level);
            case EVipLevel.Professional:
                return [EVipLevel.Flagship, EVipLevel.Professional].includes(this.userAgent.meta.account.level);
            case EVipLevel.Flagship:
                return [EVipLevel.Flagship].includes(this.userAgent.meta.account.level);
        }
    }
}